import { useState, useCallback, useEffect, useRef } from 'react';

interface UseNotificationOptions {
  autoHide?: boolean;
  duration?: number;
}

export const useNotification = (options: UseNotificationOptions = {}) => {
  const { autoHide = true, duration = 3000 } = options;
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearNotification = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const showSuccess = useCallback((message: string, customDuration?: number) => {
    clearNotification();
    setError('');
    setSuccess(message);
    
    if (autoHide) {
      timeoutRef.current = setTimeout(() => {
        setSuccess('');
      }, customDuration || duration);
    }
  }, [autoHide, duration, clearNotification]);

  const showError = useCallback((message: string, customDuration?: number) => {
    clearNotification();
    setSuccess('');
    setError(message);
    
    if (autoHide) {
      timeoutRef.current = setTimeout(() => {
        setError('');
      }, customDuration || duration);
    }
  }, [autoHide, duration, clearNotification]);

  const clearAll = useCallback(() => {
    clearNotification();
    setSuccess('');
    setError('');
  }, [clearNotification]);

  useEffect(() => {
    return () => {
      clearNotification();
    };
  }, [clearNotification]);

  return {
    success,
    error,
    setSuccess: showSuccess,
    setError: showError,
    clearAll,
  };
};

